export interface NetworkInfo {
  name: string;
  fee: number;
  time: string;
}

export interface CryptoAsset {
  symbol: string;
  name: string;
  nameAr: string;
  price: number;
  sellPrice: number;
  change24h: number;
  icon: string;
  image?: string;
  color: string;
  networks: NetworkInfo[];
  market_cap_rank?: number;
}

const PROFIT_MARGIN = 1.20;

// Fee = (2× blockchain gas for sweep+send-to-Binance) + Binance withdrawal fee + $0.50 profit
const ERC20_NETWORK: NetworkInfo = { name: "Ethereum (ERC20)", fee: 8.00, time: "5-15 دقيقة" };
const TRC20_NETWORK: NetworkInfo = { name: "Tron (TRC20)", fee: 2.50, time: "1-3 دقائق" };
const ARBITRUM_NETWORK: NetworkInfo = { name: "Arbitrum", fee: 1.00, time: "2-5 دقائق" };
const OPTIMISM_NETWORK: NetworkInfo = { name: "Optimism", fee: 1.00, time: "2-5 دقائق" };
const POLYGON_NETWORK: NetworkInfo = { name: "Polygon", fee: 0.60, time: "2-5 دقائق" };
const BASE_NETWORK: NetworkInfo = { name: "Base", fee: 0.90, time: "2-5 دقائق" };
const SOLANA_NETWORK: NetworkInfo = { name: "Solana", fee: 1.50, time: "1-2 دقيقة" };
const AVALANCHE_NETWORK: NetworkInfo = { name: "Avalanche C-Chain", fee: 0.80, time: "1-3 دقائق" };

// Known crypto metadata (icons, names, networks, colors)
export const KNOWN_CRYPTO_META: Record<string, { 
  icon: string; nameAr: string; nameEn: string; color: string; networks: NetworkInfo[] 
}> = {
  BTC: {
    icon: "₿", nameAr: "بيتكوين", nameEn: "Bitcoin", color: "hsl(var(--crypto-gold))",
    networks: [
      { name: "Bitcoin", fee: 8.00, time: "30-60 دقيقة" },
    ],
  },
  ETH: {
    icon: "Ξ", nameAr: "إيثيريوم", nameEn: "Ethereum", color: "hsl(var(--crypto-purple))",
    networks: [ERC20_NETWORK, ARBITRUM_NETWORK, OPTIMISM_NETWORK, BASE_NETWORK],
  },
  USDT: {
    icon: "₮", nameAr: "تيثر", nameEn: "Tether", color: "hsl(var(--crypto-green))",
    networks: [ERC20_NETWORK, TRC20_NETWORK, POLYGON_NETWORK, ARBITRUM_NETWORK, OPTIMISM_NETWORK, SOLANA_NETWORK, AVALANCHE_NETWORK],
  },
  USDC: {
    icon: "$", nameAr: "يو إس دي سي", nameEn: "USD Coin", color: "hsl(var(--crypto-blue))",
    networks: [ERC20_NETWORK, POLYGON_NETWORK, ARBITRUM_NETWORK, OPTIMISM_NETWORK, BASE_NETWORK, SOLANA_NETWORK, AVALANCHE_NETWORK],
  },
  BNB: {
    icon: "◈", nameAr: "بي إن بي", nameEn: "BNB", color: "hsl(var(--crypto-gold))",
    networks: [{ name: "BSC (BEP20)", fee: 1.00, time: "1-3 دقائق" }],
  },
  SOL: {
    icon: "◎", nameAr: "سولانا", nameEn: "Solana", color: "hsl(var(--crypto-purple))",
    networks: [SOLANA_NETWORK],
  },
  POL: {
    icon: "⬡", nameAr: "بوليجون", nameEn: "Polygon", color: "hsl(var(--crypto-purple))",
    networks: [POLYGON_NETWORK],
  },
  AVAX: {
    icon: "▲", nameAr: "أفالانش", nameEn: "Avalanche", color: "hsl(var(--crypto-red))",
    networks: [AVALANCHE_NETWORK],
  },
  LINK: {
    icon: "⬡", nameAr: "تشين لينك", nameEn: "Chainlink", color: "hsl(var(--crypto-blue))",
    networks: [ERC20_NETWORK],
  },
  TRX: {
    icon: "◆", nameAr: "ترون", nameEn: "Tron", color: "hsl(var(--crypto-red))",
    networks: [TRC20_NETWORK],
  },
  SHIB: {
    icon: "🐕", nameAr: "شيبا إينو", nameEn: "Shiba Inu", color: "hsl(var(--crypto-gold))",
    networks: [ERC20_NETWORK],
  },
  ARB: {
    icon: "◇", nameAr: "أربيتروم", nameEn: "Arbitrum", color: "hsl(var(--crypto-blue))",
    networks: [ARBITRUM_NETWORK, ERC20_NETWORK],
  },
  LTC: {
    icon: "Ł", nameAr: "لايتكوين", nameEn: "Litecoin", color: "hsl(var(--crypto-blue))",
    networks: [{ name: "Litecoin", fee: 1.50, time: "5-15 دقيقة" }],
  },
  AAVE: {
    icon: "👻", nameAr: "آيف", nameEn: "Aave", color: "hsl(var(--crypto-purple))",
    networks: [ERC20_NETWORK],
  },
  DAI: {
    icon: "◈", nameAr: "داي", nameEn: "Dai", color: "hsl(var(--crypto-gold))",
    networks: [ERC20_NETWORK],
  },
  PEPE: {
    icon: "🐸", nameAr: "بيبي", nameEn: "Pepe", color: "hsl(var(--crypto-green))",
    networks: [ERC20_NETWORK],
  },
  WLD: {
    icon: "🌍", nameAr: "وورلدكوين", nameEn: "Worldcoin", color: "hsl(var(--crypto-blue))",
    networks: [ERC20_NETWORK],
  },
  LEO: {
    icon: "🦁", nameAr: "ليو", nameEn: "LEO Token", color: "hsl(var(--crypto-gold))",
    networks: [ERC20_NETWORK],
  },
  PAXG: {
    icon: "🥇", nameAr: "باكسوس جولد", nameEn: "PAX Gold", color: "hsl(var(--crypto-gold))",
    networks: [ERC20_NETWORK],
  },
};

// Default network for unknown coins
const DEFAULT_NETWORKS: NetworkInfo[] = [ERC20_NETWORK];

// Color palette for coins without known meta
const COLOR_PALETTE = [
  "hsl(var(--crypto-blue))",
  "hsl(var(--crypto-purple))",
  "hsl(var(--crypto-green))",
  "hsl(var(--crypto-gold))",
  "hsl(var(--crypto-red))",
];

export function buildCryptoAsset(data: {
  symbol: string;
  name: string;
  price: number;
  sellPrice: number;
  change24h: number;
  image?: string;
  market_cap_rank?: number;
}): CryptoAsset {
  const meta = KNOWN_CRYPTO_META[data.symbol];
  const colorIndex = data.symbol.charCodeAt(0) % COLOR_PALETTE.length;
  
  return {
    symbol: data.symbol,
    name: data.name,
    nameAr: meta?.nameAr || data.name,
    price: data.price,
    sellPrice: data.sellPrice,
    change24h: data.change24h,
    icon: meta?.icon || data.symbol.charAt(0),
    image: data.image,
    color: meta?.color || COLOR_PALETTE[colorIndex],
    networks: meta?.networks || DEFAULT_NETWORKS,
    market_cap_rank: data.market_cap_rank,
  };
}

// Fallback static assets (used when API fails completely)
export const cryptoAssets: CryptoAsset[] = [
  buildCryptoAsset({ symbol: "BTC", name: "Bitcoin", price: 67542.32, sellPrice: 67542.32 * PROFIT_MARGIN, change24h: 2.34 }),
  buildCryptoAsset({ symbol: "ETH", name: "Ethereum", price: 3456.78, sellPrice: 3456.78 * PROFIT_MARGIN, change24h: 1.56 }),
  buildCryptoAsset({ symbol: "USDT", name: "Tether", price: 1.00, sellPrice: 1.00 * PROFIT_MARGIN, change24h: 0.01 }),
  buildCryptoAsset({ symbol: "BNB", name: "BNB", price: 584.23, sellPrice: 584.23 * PROFIT_MARGIN, change24h: -0.89 }),
  buildCryptoAsset({ symbol: "SOL", name: "Solana", price: 142.67, sellPrice: 142.67 * PROFIT_MARGIN, change24h: 4.21 }),
  buildCryptoAsset({ symbol: "LTC", name: "Litecoin", price: 84.50, sellPrice: 84.50 * PROFIT_MARGIN, change24h: 1.32 }),
];

export const formatCurrency = (amount: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatCrypto = (amount: number): string => {
  return amount.toFixed(8).replace(/\.?0+$/, "");
};
